// src/components/account/AppointmentsTab.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { History, Calendar, Clock, Car, MapPin, Download, ChevronDown, ChevronUp, RefreshCw, X, Ban } from 'lucide-react';
import { ref, onValue } from 'firebase/database';
import { database } from '../../firebase';
import useAuth from '../../hooks/useAuth';
import { downloadBookingReceipt } from '../../utils/pdfGenerator';
import BookingService from '../../services/BookingService';
import emailService from '../../services/emailService';
import AdminService from '../../services/AdminService';

const AppointmentsTab = () => {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAppointment, setExpandedAppointment] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [rescheduleError, setRescheduleError] = useState(null);
  const [blockedDates, setBlockedDates] = useState({});
  
  // Fetch appointments from Firebase
  useEffect(() => {
    if (user) {
      const appointmentsRef = ref(database, `users/${user.uid}/bookings`);
      const unsubscribe = onValue(appointmentsRef, (snapshot) => {
        if (snapshot.exists()) {
          const appointmentsData = snapshot.val();
          const appointmentsArray = Object.keys(appointmentsData).map(key => ({
            id: key,
            ...appointmentsData[key]
          }));
          
          // Sort by date (closest upcoming first, then past)
          appointmentsArray.sort((a, b) => {
            const dateA = new Date(a.schedule?.date);
            const dateB = new Date(b.schedule?.date);
            const now = new Date();
            
            // Both upcoming
            if (dateA > now && dateB > now) return dateA - dateB;
            // Both past
            if (dateA < now && dateB < now) return dateB - dateA;
            // A is upcoming, B is past
            if (dateA > now && dateB < now) return -1;
            // A is past, B is upcoming
            return 1;
          });
          
          setAppointments(appointmentsArray);
        } else {
          setAppointments([]);
        }
        setLoading(false);
      });
      
      return () => unsubscribe();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Load blocked dates for reschedule modal
  useEffect(() => {
    AdminService.getBlockedDates().then(setBlockedDates).catch(() => {});
  }, []);

  // Blocked-date helpers
  const toDateKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const isDayBlocked = (date) => blockedDates[toDateKey(date)]?.allDay === true;
  const getBlockedTimes = (date) => {
    const entry = blockedDates[toDateKey(date)];
    if (!entry) return [];
    if (entry.allDay) return timeSlots;
    return entry.times || [];
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // Get status badge color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Toggle appointment details
  const toggleAppointmentDetails = (id) => {
    if (expandedAppointment === id) {
      setExpandedAppointment(null);
    } else {
      setExpandedAppointment(id);
    }
  };
  
  // Generate and download PDF receipt
  const generatePDF = (appointment) => {
    try {
      setGeneratingPdf(appointment.id);
      
      // Prepare receipt data
      const receiptData = {
        id: appointment.id || `booking-${Date.now()}`,
        date: appointment.createdAt || new Date().toISOString(),
        service: appointment.service,
        customer: {
          name: user.displayName || 'Customer',
          email: user.email || 'Not provided'
        },
        vehicle: appointment.vehicle,
        scheduling: appointment.schedule,
        payment: appointment.payment
      };
      
      // Use our PDF generator utility to download the receipt
      const success = downloadBookingReceipt(receiptData);
      
      if (!success) {
        throw new Error("Failed to generate PDF");
      }
    } catch (err) {
      console.error("Error generating PDF:", err);
      alert("There was a problem generating your receipt. Please try again later.");
    } finally {
      setGeneratingPdf(null);
    }
  };
  
  // Available time slots
  const timeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'
  ];

  // Get next 7 available dates
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  // Open reschedule modal
  const openReschedule = (appointment) => {
    setRescheduleTarget(appointment);
    setRescheduleDate(null);
    setRescheduleTime(null);
    setRescheduleError(null);
  };

  // Confirm reschedule
  const confirmReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime || !rescheduleTarget) return;

    setRescheduling(true);
    setRescheduleError(null);

    try {
      const newDateISO = rescheduleDate.toISOString();

      await BookingService.rescheduleBooking(
        user.uid,
        rescheduleTarget.id,
        newDateISO,
        rescheduleTime
      );

      // Send confirmation email
      try {
        await emailService.sendBookingConfirmation({
          id: rescheduleTarget.id,
          customer: { email: user.email, name: user.displayName },
          service: rescheduleTarget.service,
          schedule: { date: newDateISO, time: rescheduleTime },
          vehicle: rescheduleTarget.vehicle,
          payment: rescheduleTarget.payment
        });
      } catch (emailErr) {
        console.error('Reschedule email failed:', emailErr);
      }

      setRescheduleTarget(null);
    } catch (err) {
      console.error('Reschedule failed:', err);
      setRescheduleError('Failed to reschedule. Please try again.');
    } finally {
      setRescheduling(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Your Appointments</h2>
        <Link to="/book">
          <button className="bg-gradient-to-r from-[#3A98B9] to-[#2A87A8] text-white px-4 py-2 rounded-lg text-sm font-medium">
            Book New Service
          </button>
        </Link>
      </div>
      
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3A98B9] mx-auto mb-2"></div>
          <p>Loading your appointments...</p>
        </div>
      ) : appointments.length > 0 ? (
        <div className="space-y-4">
          {appointments.map(appointment => {
            const isPast = new Date(appointment.schedule?.date) < new Date();
            const isExpanded = expandedAppointment === appointment.id;
            
            return (
              <div 
                key={appointment.id} 
                className={`border rounded-lg overflow-hidden ${
                  isPast ? 'border-gray-200 opacity-75' : 'border-[#3A98B9]/30'
                }`}
              >
                <div className={`p-4 ${isPast ? 'bg-gray-50' : 'bg-[#3A98B9]/5'}`}>
                  <div className="flex justify-between items-center flex-wrap gap-2">
                    <div className="flex items-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                        {appointment.status || 'Pending'}
                      </span>
                      <h3 className="ml-3 font-medium">{appointment.service?.title}</h3>
                    </div>
                    <div className="text-sm text-gray-600">
                      Booking ID: #{appointment.id.slice(-6).toUpperCase()}
                    </div>
                  </div>
                </div>
                
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start">
                      <Calendar className="h-5 w-5 text-[#3A98B9] mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Appointment Date</p>
                        <p className="text-gray-600">{formatDate(appointment.schedule?.date)}</p>
                        <p className="text-sm text-gray-500">
                          <Clock className="h-3 w-3 inline-block mr-1" />
                          {appointment.schedule?.time}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Car className="h-5 w-5 text-[#3A98B9] mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Vehicle</p>
                        <p className="text-gray-600">
                          {appointment.vehicle?.year} {appointment.vehicle?.make} {appointment.vehicle?.model}
                        </p>
                        {appointment.vehicle?.color && (
                          <p className="text-sm text-gray-500">{appointment.vehicle.color}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex items-start">
                    <MapPin className="h-5 w-5 text-[#3A98B9] mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Service Location</p>
                      <p className="text-gray-600">{appointment.vehicle?.location || 'Not specified'}</p>
                    </div>
                  </div>
                  
                  {/* Expanded details section */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium mb-3">Payment Details</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Service</span>
                            <span>${appointment.payment?.subtotal?.toFixed(2) || appointment.service?.price?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Tax</span>
                            <span>${appointment.payment?.tax?.toFixed(2) || '0.00'}</span>
                          </div>
                          {appointment.payment?.giftCardAmount > 0 && (
                            <div className="flex justify-between text-green-600">
                              <span>Gift Card</span>
                              <span>-${appointment.payment.giftCardAmount.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-medium pt-2 border-t border-gray-200">
                            <span>Total</span>
                            <span>${appointment.payment?.total?.toFixed(2) || appointment.service?.price?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div className="text-right text-sm text-gray-500">
                            {appointment.payment?.method === 'credit_card_and_gift_card' 
                              ? `Paid with card ending in ${appointment.payment?.last4 || '****'} and gift card` 
                              : appointment.payment?.method === 'gift_card'
                                ? 'Paid with gift card'
                                : `Paid with card ending in ${appointment.payment?.last4 || '****'}`}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Amount</p>
                      <p className="font-semibold">${appointment.payment?.total?.toFixed(2) || appointment.service?.price?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div className="flex space-x-2">
                      {!isPast && appointment.status !== 'cancelled' && (
                        <button
                          onClick={() => openReschedule(appointment)}
                          className="px-3 py-1 bg-gradient-to-r from-[#3A98B9] to-[#2A87A8] text-white rounded-md text-sm hover:shadow-md flex items-center"
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Reschedule
                        </button>
                      )}
                      <button
                        onClick={() => toggleAppointmentDetails(appointment.id)}
                        className="px-3 py-1 border border-[#3A98B9] text-[#3A98B9] rounded-md text-sm hover:bg-[#3A98B9]/5 flex items-center"
                      >
                        {isExpanded ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
                        {isExpanded ? 'Hide Details' : 'View Details'}
                      </button>

                      <button
                        onClick={() => generatePDF(appointment)}
                        disabled={generatingPdf === appointment.id}
                        className="px-3 py-1 border border-[#3A98B9] text-[#3A98B9] rounded-md text-sm hover:bg-[#3A98B9]/5 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {generatingPdf === appointment.id ? (
                          <>
                            <div className="animate-spin h-4 w-4 border-b-2 border-[#3A98B9] rounded-full mr-1"></div>
                            Generating...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-1" />
                            Receipt
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-lg">
          <div className="mx-auto w-16 h-16 rounded-full bg-[#3A98B9]/10 flex items-center justify-center mb-4">
            <History className="h-8 w-8 text-[#3A98B9]" />
          </div>
          <h3 className="text-xl font-medium text-gray-800 mb-2">No appointments yet</h3>
          <p className="text-gray-600 max-w-md mx-auto mb-6">
            Your appointment history will appear here once you book your first service.
          </p>
          <Link to="/book">
            <button className="bg-gradient-to-r from-[#3A98B9] to-[#2A87A8] text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-all duration-300">
              Schedule Your First Appointment
            </button>
          </Link>
        </div>
      )}

      {/* Reschedule Modal */}
      {rescheduleTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-5 border-b">
              <h3 className="text-lg font-semibold">Reschedule Appointment</h3>
              <button onClick={() => setRescheduleTarget(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5">
              {/* Current appointment info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-5">
                <p className="text-sm text-gray-500 mb-1">Current Appointment</p>
                <p className="font-medium">{rescheduleTarget.service?.title}</p>
                <p className="text-sm text-gray-600">
                  {formatDate(rescheduleTarget.schedule?.date)} at {rescheduleTarget.schedule?.time}
                </p>
              </div>

              {/* Date picker */}
              <h4 className="font-medium text-gray-700 mb-3">Select New Date</h4>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-5">
                {getAvailableDates().map((date, i) => {
                  const blocked = isDayBlocked(date);
                  return (
                    <button
                      key={i}
                      disabled={blocked}
                      className={`border p-3 rounded-lg text-center transition-all ${
                        blocked
                          ? 'border-red-200 bg-red-50 text-red-400 cursor-not-allowed'
                          : rescheduleDate && rescheduleDate.toDateString() === date.toDateString()
                            ? 'border-[#3A98B9] bg-[#3A98B9]/10'
                            : 'border-gray-200 hover:border-[#3A98B9]/50'
                      }`}
                      onClick={() => { if (!blocked) { setRescheduleDate(date); setRescheduleTime(null); } }}
                    >
                      {blocked && <Ban className="h-3 w-3 mx-auto mb-0.5 text-red-400" />}
                      <div className="text-xs text-gray-500">{date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                      <div className="text-lg font-medium">{date.getDate()}</div>
                      <div className="text-xs text-gray-500">
                        {blocked ? 'Unavailable' : date.toLocaleDateString('en-US', { month: 'short' })}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Time picker */}
              <h4 className="font-medium text-gray-700 mb-3">Select New Time</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
                {timeSlots.map((time, i) => {
                  const blocked = rescheduleDate ? getBlockedTimes(rescheduleDate).includes(time) : false;
                  return (
                    <button
                      key={i}
                      disabled={blocked}
                      className={`border p-2 rounded-lg text-center text-sm transition-all ${
                        blocked
                          ? 'border-red-200 bg-red-50 text-red-400 cursor-not-allowed line-through'
                          : rescheduleTime === time
                            ? 'border-[#3A98B9] bg-[#3A98B9]/10 font-medium'
                            : 'border-gray-200 hover:border-[#3A98B9]/50'
                      }`}
                      onClick={() => { if (!blocked) setRescheduleTime(time); }}
                    >
                      {time}
                    </button>
                  );
                })}
              </div>

              {rescheduleError && (
                <p className="text-red-600 text-sm mb-4">{rescheduleError}</p>
              )}
            </div>

            <div className="flex justify-end gap-3 p-5 border-t">
              <button
                onClick={() => setRescheduleTarget(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmReschedule}
                disabled={!rescheduleDate || !rescheduleTime || rescheduling}
                className="px-4 py-2 bg-gradient-to-r from-[#3A98B9] to-[#2A87A8] text-white rounded-lg text-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {rescheduling ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-b-2 border-white rounded-full mr-2"></div>
                    Rescheduling...
                  </>
                ) : (
                  'Confirm Reschedule'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentsTab;